import { useEffect, useRef, useState } from 'react';
import { OptionRow } from '../components/Controls.tsx';
import { api } from '../lib/api.ts';
import { errorMessage } from '../../shared/errors.ts';
import type { PiLoginState, PiOAuthProvider } from '../../shared/api.ts';

/**
 * SIGNING PI INTO A SUBSCRIPTION, IN THE DRAWER.
 *
 * Pi's `/login` is an interactive TUI, so before this the only way to give the
 * engine a plan credential was a shell on the server — `docker exec` on the
 * hosted studio, which is not a setup step you can hand to the person who owns
 * the ChatGPT plan. The server drives Pi's own flow (routes/pi-auth.ts) and this
 * renders it: pick the provider, press the button, follow what Pi says.
 *
 * THE PROVIDER LIST IS THE SERVER'S, and the server's is Pi's. A select that
 * hardcoded ChatGPT would have to be edited every time Pi adds a subscription,
 * which is precisely the coupling this engine exists to avoid — so the list is
 * fetched, and the field falls back to FREE TEXT whenever it cannot be (no pi on
 * the server, an old CLI, an id Pi has not shipped yet). The row never stops
 * being configurable because a convenience failed.
 *
 * THE FLOW IS POLLED, not streamed: three or four state changes over about a
 * minute of a person reading a code. Two shapes arrive from it, and both are
 * Pi's rather than ours — `device_code` (a code to type at a URL, which is the
 * only shape that can work on a remote server, since a browser flow's callback
 * would land on the SERVER's localhost) and `auth_url` (open this, used when the
 * studio is running on the same machine as the browser). A `prompt` means Pi is
 * blocked and needs an answer typed back.
 */
export function PiLogin({
  provider,
  onProvider,
  /** Called after a sign-in or sign-out lands, so the drawer can re-read the
   *  engine block — availability, and the gate's issue line, both change. */
  onChanged,
  /** The build engine's issue string, or null. Shown as the row's hint when the
   *  engine cannot start, because "no login for X" is the sentence that explains
   *  the button. */
  issue,
}: {
  provider: string;
  onProvider: (next: string) => void;
  onChanged: () => void;
  issue?: string | null;
}) {
  const [providers, setProviders] = useState<PiOAuthProvider[] | null>(null);
  // What the SERVER currently runs Pi on, which is not the same as what the
  // field says: this row can sign in before the drawer is saved, and a login
  // that worked while the setting still names another provider is the one
  // confusing state this flow can reach.
  const [selected, setSelected] = useState('');
  const [listIssue, setListIssue] = useState<string | null>(null);
  const [home, setHome] = useState<string>('');
  const [session, setSession] = useState<PiLoginState | null>(null);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The poll, kept in a ref so every exit path (done, error, cancel, unmount)
  // stops it — a settings drawer left open must not poll forever.
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .get<{ providers: PiOAuthProvider[]; issue?: string; home?: string; selected?: string }>(
        '/api/settings/pi/providers',
      )
      .then((d) => {
        if (!alive) return;
        setProviders(d.providers || []);
        setListIssue(d.issue || null);
        setHome(d.home || '');
        setSelected(d.selected || '');
      })
      .catch((e) => alive && setListIssue(errorMessage(e)));
    // A sign-in may already be running — a reopened drawer, a second tab.
    api
      .get<{ session: PiLoginState | null }>('/api/settings/pi/login')
      .then((d) => alive && d.session?.status === 'running' && startPolling(d.session))
      .catch(() => {});
    return () => {
      alive = false;
      if (timer.current) clearInterval(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only: the poll owns its own lifecycle
  }, []);

  function stopPolling() {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }

  function startPolling(initial: PiLoginState) {
    setSession(initial);
    stopPolling();
    timer.current = setInterval(async () => {
      try {
        const d = await api.get<{ session: PiLoginState | null }>(`/api/settings/pi/login?id=${initial.id}`);
        if (!d.session) return stopPolling();
        setSession(d.session);
        if (d.session.status !== 'running') {
          stopPolling();
          // The list carries `loggedIn`, and the engine block carries the gate's
          // verdict — both are stale the moment this lands.
          if (d.session.status === 'done') {
            api
              .get<{ providers: PiOAuthProvider[] }>('/api/settings/pi/providers')
              .then((p) => setProviders(p.providers || []))
              .catch(() => {});
            onChanged();
          }
        }
      } catch {
        /* a dropped poll is not a failed login: the next tick asks again */
      }
    }, 1000);
  }

  async function signIn() {
    setBusy(true);
    setErr(null);
    try {
      const d = await api.post<{ session: PiLoginState }>('/api/settings/pi/login', { provider });
      startPolling(d.session);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function sendAnswer() {
    if (!session) return;
    setBusy(true);
    try {
      await api.post('/api/settings/pi/login/answer', { id: session.id, value: answer });
      setAnswer('');
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  async function cancel() {
    if (!session) return;
    stopPolling();
    try {
      await api.post('/api/settings/pi/login/cancel', { id: session.id });
    } catch {
      /* it may already have finished; the state below is what matters */
    }
    setSession(null);
  }

  async function signOut() {
    setBusy(true);
    setErr(null);
    try {
      await api.post('/api/settings/pi/logout', { provider });
      const p = await api.get<{ providers: PiOAuthProvider[] }>('/api/settings/pi/providers');
      setProviders(p.providers || []);
      onChanged();
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const known = providers?.find((p) => p.id === provider);
  const signedIn = known?.loggedIn === true;
  const running = session?.status === 'running';
  const device = [...(session?.events || [])].reverse().find((e) => e.type === 'device_code');
  const url = [...(session?.events || [])].reverse().find((e) => e.type === 'auth_url');
  const progress = [...(session?.events || [])].reverse().find((e) => e.type === 'progress' || e.type === 'info');

  return (
    <>
      <OptionRow
        label="Login"
        sub
        stack
        hint={
          listIssue
            ? // ONE diagnosis, not two. This row and the engine's head hint are
              // both about the same server, so when the CLI is missing the head
              // says so (piBuildIssue checks the binary first) and this says what
              // to do about it — the pair used to contradict each other, one
              // telling you to run a command the other had just said was not
              // installed.
              `${listIssue} Once it is installed, sign in here — or on the server itself: \`PI_CODING_AGENT_DIR=${home || '<state dir>/pi-build-home'} pi\`, then \`/login\`.`
            : signedIn
              ? `Signed in. Builds and chat run on this credential, and nothing is charged to the Pi key below. ${
                  known?.isSubscription === false
                    ? 'This provider bills its own way (credits, or per token) — "not metered by this studio" is the honest description, not "free".'
                    : 'What the plan itself allows is between you and the provider.'
                }`
              : issue ||
                'Pick the provider this server should run Pi on, then sign in. The credential is written into the studio’s own Pi home, never your ~/.pi.'
        }
      >
        <div className="cred">
          {providers && providers.length > 0 ? (
            <select
              value={providers.some((p) => p.id === provider) ? provider : '__custom'}
              onChange={(e) => onProvider(e.target.value === '__custom' ? '' : e.target.value)}
              aria-label="Pi subscription provider"
              disabled={running}
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.loggedIn ? ' · signed in' : ''}
                </option>
              ))}
              {/* The escape hatch stays: an id Pi ships tomorrow must be
                  typeable today. */}
              <option value="__custom">Another provider id…</option>
            </select>
          ) : null}
          {(!providers || providers.length === 0 || !providers.some((p) => p.id === provider)) && (
            <input
              type="text"
              value={provider}
              onChange={(e) => onProvider(e.target.value)}
              placeholder="openai-codex"
              aria-label="Pi subscription provider id"
              spellCheck={false}
              disabled={running}
            />
          )}
          {!running && (
            <button className="btn btn--sm" disabled={busy || !provider.trim() || Boolean(listIssue)} onClick={signIn}>
              {signedIn ? 'Sign in again' : 'Sign in'}
            </button>
          )}
          {!running && signedIn && (
            <button className="btn btn--sm btn--ghost" disabled={busy} onClick={signOut}>
              Sign out
            </button>
          )}
          {running && (
            <button className="btn btn--sm btn--ghost" onClick={cancel}>
              Cancel
            </button>
          )}
        </div>
        {err && <p className="small error">{err}</p>}
        {/* A warning must always offer a way out: the login landed, but the
            SAVED setting still names something else, so a build would run on
            the other provider until this drawer is saved. */}
        {signedIn && selected && selected !== provider && (
          <p className="small muted">
            Signed in to {provider}, but this server still builds on {selected}. Press Save below to switch it.
          </p>
        )}
      </OptionRow>

      {/* What Pi is asking for, verbatim in shape if not in words. The device
          code is the shape that works on a remote server, so it leads. */}
      {running && device && device.type === 'device_code' && (
        <OptionRow
          label="Enter this code"
          sub
          stack
          hint={`Open ${device.verificationUri} and enter the code. This page will notice when it is done.`}
        >
          <div className="cred">
            <code className="cred__hint">{device.userCode}</code>
            <a className="btn btn--sm" href={device.verificationUri} target="_blank" rel="noreferrer">
              Open the page
            </a>
          </div>
        </OptionRow>
      )}

      {running && url && url.type === 'auth_url' && !device && (
        <OptionRow
          label="Authorise"
          sub
          stack
          hint={
            url.instructions ||
            'Open this to authorise. It only completes if this browser can reach the server’s own localhost — on a remote studio, use a provider that offers a device code instead.'
          }
        >
          <a className="btn btn--sm" href={url.url} target="_blank" rel="noreferrer">
            Open the sign-in page
          </a>
        </OptionRow>
      )}

      {running && session?.prompt && (
        <OptionRow label="Pi is asking" sub stack hint={session.prompt.message}>
          {session.prompt.kind === 'select' ? (
            <div className="cred">
              {(session.prompt.options || []).map((o) => (
                <button
                  key={o.id}
                  className="btn btn--sm"
                  disabled={busy}
                  onClick={() => {
                    setAnswer(o.id);
                    void api
                      .post('/api/settings/pi/login/answer', { id: session.id, value: o.id })
                      .catch((e) => setErr(errorMessage(e)));
                  }}
                  title={o.description}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="cred">
              <input
                type={session.prompt.kind === 'secret' ? 'password' : 'text'}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={session.prompt.placeholder || ''}
                aria-label={session.prompt.message}
                spellCheck={false}
              />
              <button className="btn btn--sm" disabled={busy || !answer.trim()} onClick={sendAnswer}>
                Send
              </button>
            </div>
          )}
        </OptionRow>
      )}

      {running && !device && !url && !session?.prompt && (
        <p className="small muted">{progress && 'message' in progress ? progress.message : 'Starting the sign-in…'}</p>
      )}
      {session?.status === 'error' && <p className="small error">Sign-in failed: {session.error}</p>}
      {session?.status === 'done' && <p className="small muted">Signed in to {session.provider}.</p>}
    </>
  );
}
